import { STORIES, getStory } from '@/content/stories';
import type { Story } from '@/contracts/content';

/**
 * Story of the Week (spec §8.4, F17).
 *
 * An editorial selection with a configured start, end and time zone. It is a
 * suggestion and nothing more:
 *
 *  - It never expires a child's creations.
 *  - It never requires participation on a particular day.
 *  - Missing a week costs nothing, and the app says so.
 *
 * Resolved from a schedule rather than computed from "the current week", so
 * an editor decides what is featured and when — the same shape a server-side
 * schedule will have when there is one.
 */

export interface EditorialEvent {
  id: string;
  storyId: string;
  /** Inclusive ISO date. */
  startsOn: string;
  /** Exclusive ISO date. */
  endsOn: string;
  /** IANA zone the dates are interpreted in. */
  timeZone: string;
  /** Optional note shown to a parent, never to a child as an instruction. */
  parentNote?: string;
}

/**
 * The authored schedule. Dates are deliberately explicit rather than
 * generated, so what is featured is an editorial decision on the record.
 */
export const EDITORIAL_SCHEDULE: EditorialEvent[] = [
  {
    id: 'sotw_2026_w37',
    storyId: 'story_pip_loudest_night',
    startsOn: '2026-09-07',
    endsOn: '2026-09-14',
    timeZone: 'Europe/London',
    parentNote: 'A storm story that lands somewhere calm — good for a windy night.',
  },
  {
    id: 'sotw_2026_w38',
    storyId: 'story_pip_paper_moon',
    startsOn: '2026-09-14',
    endsOn: '2026-09-21',
    timeZone: 'Europe/London',
    parentNote: 'The one where making something beats finding it.',
  },
  {
    id: 'sotw_2026_w39',
    storyId: 'story_pip_small_visitor',
    startsOn: '2026-09-21',
    endsOn: '2026-09-28',
    timeZone: 'Europe/London',
    parentNote: 'A quiet one for a morning, about noticing what you walk past.',
  },
  {
    id: 'sotw_2026_w40',
    storyId: 'story_pip_loudest_night',
    startsOn: '2026-09-28',
    endsOn: '2026-10-05',
    timeZone: 'Europe/London',
  },
];

/** The date, as YYYY-MM-DD, in the event's own time zone. */
function localDate(now: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    // An unknown zone must not break the app; fall back to the device date.
    return now.toISOString().slice(0, 10);
  }
}

export interface FeaturedStory {
  event: EditorialEvent;
  story: Story;
}

/** The story featured right now, or null when the schedule has a gap. */
export function storyOfTheWeek(now: Date = new Date()): FeaturedStory | null {
  for (const event of EDITORIAL_SCHEDULE) {
    const today = localDate(now, event.timeZone);
    if (today >= event.startsOn && today < event.endsOn) {
      const story = getStory(event.storyId);
      // A schedule entry pointing at unpublished or missing content is
      // ignored rather than surfaced as a broken card.
      if (story?.currentVersion.published) return { event, story };
    }
  }
  return null;
}

/**
 * "Same story, different imaginations" (F17) — the collection view groups the
 * stories that share a collection, so a child can see the others by the same
 * characters. It is a browse aid, not a completion requirement.
 */
export function storiesInCollection(collection: string): Story[] {
  return STORIES.filter((s) => s.collection === collection);
}

export function collections(): Array<{ name: string; stories: Story[] }> {
  const names = [...new Set(STORIES.map((s) => s.collection))];
  return names.map((name) => ({ name, stories: storiesInCollection(name) }));
}
